import MultiSelectDropdown from 'components/common/MultiSelectDropdown';
import { DISPLAY_TIMING_RADIO } from 'features/MenuList/constants';
import { DisplayTimingGroupDetail, Menu } from 'features/MenuList/models/menu';
import { ChangeEvent } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface DisplayTimingGroupOption {
  id: number;
  group_name: string;
}

interface Props {
  selectedMenu: Menu;
  listDisplayControlGroup: { key: string; value: number }[];
  listDisplayTimingGroup: DisplayTimingGroupOption[];
  listDisplayTimingGroupDetails: Map<number, DisplayTimingGroupDetail>;
  onMenuChange: (updates: Partial<Menu>) => void;
  onControlGroupChange: (values: number[]) => void;
}

const OVERLAY_LANGS = [
  { key: 'ja', label: '日本語', placeholder: 'この時間帯は注文できません' },
  { key: 'en', label: '英語', placeholder: 'Orders cannot be placed during this time.' },
  { key: 'zh_hans', label: '中国語(簡体字)', placeholder: '此时间段无法点餐。' },
  { key: 'zh_hant', label: '中国語(繫体字)', placeholder: '此時段無法點餐。' },
  { key: 'ko', label: '韓国語', placeholder: '이 시간대에는 주문하실 수 없습니다.' },
  { key: 'vi', label: 'ベトナム語', placeholder: 'Không thể đặt món trong khung giờ này.' },
];

const CONDITION_SUFFIX: Record<string, string> = {
  include: 'のみ',
  exclude: '以外',
};

function formatWeekdaySummary(summary: string, condition: string): string {
  if (!summary) return '';
  const suffix = CONDITION_SUFFIX[condition] ?? '';
  if (summary === '月火水木金') return `月〜金${suffix}`;
  if (summary === '土日') return `土日${suffix}`;
  if (summary === '月火水木金土日') return `毎日`;
  if (summary === '月火水木金土日祝') return `毎日（祝日含む）`;
  return `${summary}${suffix}`;
}

function formatScheduleSummary(detail: DisplayTimingGroupDetail, t: (k: string) => string): string {
  const parts: string[] = [];
  if (detail.period_summary) {
    const suffix = CONDITION_SUFFIX[detail.period_condition] ?? '';
    parts.push(`${detail.period_summary}${suffix}`);
  }
  if (detail.monthly_days.length > 0) {
    const days = detail.monthly_days.join(',');
    const suffix = CONDITION_SUFFIX[detail.monthly_day_condition] ?? '';
    parts.push(`毎月${days}日${suffix}`);
  }
  return parts.length > 0 ? parts.join('、') : t('menuDetailFeature.Text.conditions_schedule_none');
}

function formatTimeSummary(
  detail: DisplayTimingGroupDetail,
  t: (k: string) => string
): {
  time: string;
  grace: string;
} {
  let time = t('menuDetailFeature.Text.conditions_time_none');
  if (detail.time_summary) {
    const suffix = CONDITION_SUFFIX[detail.time_condition] ?? '';
    time = `${detail.time_summary}${suffix}`;
  }
  const extendTime = detail.time_summary?.includes('ランチ')
    ? detail.given_lunch_extend_time
    : detail.given_range_extend_time;
  const grace =
    detail.time_summary && detail.time_condition === 'include' && extendTime > 0
      ? `${t('menuDetailFeature.Text.conditions_grace')}${extendTime}${t(
          'menuDetailFeature.Text.conditions_grace_min'
        )}`
      : '';
  return { time, grace };
}

export default function DisplayTimingSection({
  selectedMenu,
  listDisplayControlGroup,
  listDisplayTimingGroup,
  listDisplayTimingGroupDetails,
  onMenuChange,
  onControlGroupChange,
}: Props) {
  const { t } = useTranslation();

  const radioValue =
    selectedMenu.display_timing === DISPLAY_TIMING_RADIO.CONTROL_GROUP
      ? DISPLAY_TIMING_RADIO.CONTROL_GROUP
      : selectedMenu.display_timing === DISPLAY_TIMING_RADIO.TIMING_GROUP
      ? DISPLAY_TIMING_RADIO.TIMING_GROUP
      : DISPLAY_TIMING_RADIO.NO_CONTROL;

  const handleRadioChange = (value: number) => {
    const firstGroup = listDisplayTimingGroup[0];
    const firstDetail = firstGroup ? listDisplayTimingGroupDetails.get(firstGroup.id) : undefined;
    const autoSelect =
      value === DISPLAY_TIMING_RADIO.TIMING_GROUP && firstGroup && firstDetail
        ? {
            display_timing_group_id: firstGroup.id,
            display_timing_group_detail: { ...firstDetail, is_active: true },
          }
        : {};
    onMenuChange({
      display_timing: value,
      is_disable_control_group: value !== DISPLAY_TIMING_RADIO.CONTROL_GROUP,
      ...(value !== DISPLAY_TIMING_RADIO.TIMING_GROUP && {
        display_timing_group_id: null,
        display_timing_group_detail: null,
        display_timing_group_overlay_enabled: false,
        display_timing_group_overlay_message: '',
      }),
      ...autoSelect,
    });
  };

  const handleGroupSelect = (e: ChangeEvent<HTMLSelectElement>) => {
    const groupId = e.target.value ? parseInt(e.target.value) : null;
    const raw = groupId ? listDisplayTimingGroupDetails.get(groupId) ?? null : null;
    const detail = raw ? { ...raw, is_active: true } : null;
    onMenuChange({
      display_timing_group_id: groupId,
      display_timing_group_detail: detail,
    });
  };

  const handleOverlayEnabledChange = (e: ChangeEvent<HTMLInputElement>) => {
    onMenuChange({
      display_timing_group_overlay_enabled: e.target.checked,
      ...(!e.target.checked && { display_timing_group_overlay_message: '' }),
      ...(e.target.checked &&
        !selectedMenu.display_timing_group_overlay_message && {
          display_timing_group_overlay_message: t('menuDetailFeature.Text.overlay_default_message'),
        }),
    });
  };

  const groupDetail = selectedMenu.display_timing_group_detail;

  return (
    <div className="display-timing-radios pt-2">
      {/* <div className="small text-muted mb-2">{t('menuDetailFeature.Text.control_condition')}：</div> */}

      {/* Radio 1: 制御なし（常時表示） */}
      <Form.Check
        type="radio"
        name="display_timing_radio"
        id="display-timing-no-control"
        checked={radioValue === DISPLAY_TIMING_RADIO.NO_CONTROL}
        label={`${t('menuDetailFeature.Text.no_control')}（常時表示）`}
        onChange={() => handleRadioChange(DISPLAY_TIMING_RADIO.NO_CONTROL)}
      />

      {/* Radio 2: 表示タイミンググループ */}
      <Form.Check
        type="radio"
        name="display_timing_radio"
        id="display-timing-timing-group"
        checked={radioValue === DISPLAY_TIMING_RADIO.TIMING_GROUP}
        label={t('menuDetailFeature.Text.display_timing_group_radio')}
        onChange={() => handleRadioChange(DISPLAY_TIMING_RADIO.TIMING_GROUP)}
      />
      {radioValue === DISPLAY_TIMING_RADIO.TIMING_GROUP && (
        <div className="timing-group-content">
          {listDisplayTimingGroup.length === 0 ? (
            <div className="small text-muted mb-3">
              {t('menuDetailFeature.Text.no_timing_group_available')}
              <Link
                to="/business-type/display-timing-group"
                className="ms-1"
                style={{ fontWeight: 'bold', textDecoration: 'underline' }}
              >
                {t('menuDetailFeature.Text.display_timing_group_link')}
              </Link>
            </div>
          ) : (
            <>
              {/* Group select + button — grid aligned */}
              <div className="timing-group-grid mb-3">
                <Form.Label className="mb-0" style={{ whiteSpace: 'nowrap' }}>
                  {t('menuDetailFeature.Text.display_timing_group_select')}
                </Form.Label>
                <Form.Select
                  value={selectedMenu.display_timing_group_id ?? ''}
                  onChange={handleGroupSelect}
                  style={{ maxWidth: 280 }}
                >
                  {selectedMenu.display_timing_group_id && groupDetail && !groupDetail.is_active && (
                    <option value={selectedMenu.display_timing_group_id} disabled>
                      {groupDetail.group_name}（無効）
                    </option>
                  )}
                  {listDisplayTimingGroup.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.group_name}
                    </option>
                  ))}
                </Form.Select>
                <div />
                <Link
                  to="/business-type/display-timing-group"
                  style={{ fontWeight: 'bold', textDecoration: 'underline' }}
                >
                  {t('menuDetailFeature.Text.display_timing_group_link')}
                </Link>
              </div>

              {/* Preview box — structured conditions */}
              {groupDetail && (
                <div className="conditions-preview">
                  <div className="conditions-preview__title">
                    「{groupDetail.group_name}」{t('menuDetailFeature.Text.conditions_title')}
                  </div>
                  <table className="conditions-preview__table">
                    <tbody>
                      <tr>
                        <td className="conditions-preview__label">{t('menuDetailFeature.Text.conditions_weekday')}</td>
                        <td className="conditions-preview__value">
                          <span
                            className="badge rounded-pill"
                            style={{ backgroundColor: '#d0d9f6', color: '#2d5bda', fontSize: '0.9rem' }}
                          >
                            {formatWeekdaySummary(groupDetail.weekday_summary, groupDetail.weekday_condition)}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td className="conditions-preview__label">{t('menuDetailFeature.Text.conditions_schedule')}</td>
                        <td className="conditions-preview__value">{formatScheduleSummary(groupDetail, t)}</td>
                      </tr>
                      <tr>
                        <td className="conditions-preview__label">{t('menuDetailFeature.Text.conditions_time')}</td>
                        <td className="conditions-preview__value">
                          {(() => {
                            const { time, grace } = formatTimeSummary(groupDetail, t);
                            return (
                              <>
                                {time !== t('menuDetailFeature.Text.conditions_time_none') ? (
                                  <span
                                    className="badge rounded-pill"
                                    style={{ backgroundColor: '#f8d7da', color: '#c0392b', fontSize: '0.9rem' }}
                                  >
                                    {time}
                                  </span>
                                ) : (
                                  <span>{time}</span>
                                )}
                                {grace && <span className="ms-2">{grace}</span>}
                              </>
                            );
                          })()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              {/* Conditions note */}
              <div className="small text-muted mb-3">{t('menuDetailFeature.Text.conditions_note')}</div>

              {/* 非表示オプション — bordered section */}
              <div className="overlay-section">
                <div className="overlay-section__header">
                  <span className="fw-bold">{t('menuDetailFeature.Text.overlay_option_label')}</span>
                  <span className="fw-normal text-muted ms-2">
                    （{t('menuDetailFeature.Text.overlay_option_subtitle')}）
                  </span>
                </div>
                <div className="overlay-section__body">
                  <Form.Check
                    type="checkbox"
                    id="overlay-enabled"
                    checked={selectedMenu.display_timing_group_overlay_enabled ?? false}
                    onChange={handleOverlayEnabledChange}
                    className="mb-1"
                    label={
                      <span>
                        <strong>{t('menuDetailFeature.Text.overlay_show')}</strong>
                        <span className="ms-1">— {t('menuDetailFeature.Text.overlay_show_desc')}</span>
                      </span>
                    }
                  />
                  <div className="small text-muted ms-4 mb-2">{t('menuDetailFeature.Text.overlay_uncheck_note')}</div>

                  {/* Comment section — multi-language, disabled when unchecked */}
                  <div className="border rounded p-2 ms-4 mt-2" style={{ backgroundColor: '#fafafa' }}>
                    <div className="small text-muted mb-1">
                      {t('menuDetailFeature.Text.overlay_comment_label')}（
                      {t('menuDetailFeature.Text.overlay_comment_subtitle')}）
                    </div>
                    {OVERLAY_LANGS.map(({ key, label, placeholder }) => (
                      <div key={key} className="d-flex align-items-center mb-1">
                        <span className="small text-muted me-2" style={{ minWidth: 80 }}>
                          {label}
                        </span>
                        <Form.Control
                          size="sm"
                          value={
                            (selectedMenu as unknown as Record<string, string>)[
                              `display_timing_group_overlay_message_${key}`
                            ] ?? ''
                          }
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            onMenuChange({
                              [`display_timing_group_overlay_message_${key}`]: e.target.value,
                            } as unknown as Partial<Menu>)
                          }
                          placeholder={placeholder}
                          disabled={!selectedMenu.display_timing_group_overlay_enabled}
                        />
                      </div>
                    ))}
                    <div className="small text-muted mt-1">{t('menuDetailFeature.Text.overlay_default_note')}</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Radio 3: 表示制御グループ */}
      <Form.Check
        type="radio"
        name="display_timing_radio"
        id="display-timing-control-group"
        checked={radioValue === DISPLAY_TIMING_RADIO.CONTROL_GROUP}
        label={t('menuDetailFeature.Text.display_control_group_radio')}
        onChange={() => handleRadioChange(DISPLAY_TIMING_RADIO.CONTROL_GROUP)}
      />
      {radioValue === DISPLAY_TIMING_RADIO.CONTROL_GROUP && (
        <div className="control-group-content">
          {/* Horizontal label + dropdown */}
          <div className="d-flex align-items-center mb-2">
            <Form.Label className="mb-0 me-3" style={{ whiteSpace: 'nowrap' }}>
              {t('menuDetailFeature.Text.display_control_group_radio')}：
            </Form.Label>
            <div style={{ flex: 1 }}>
              <MultiSelectDropdown
                defaultText={'選択無し'}
                options={listDisplayControlGroup}
                selectedValues={selectedMenu?.display_control_groups || []}
                onChange={onControlGroupChange}
                isDisabled={false}
              />
            </div>
          </div>
          <div className="small text-muted" dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.note3') }} />
        </div>
      )}
    </div>
  );
}
